import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { api, setToken } from '../api/client';

interface Inspector {
  id: string;
  display_name: string;
  is_admin: boolean;
}

interface AuthContextType {
  inspector: Inspector | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (pin: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  inspector: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [inspector, setInspector] = useState<Inspector | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('skd_auth_token');
    if (token) {
      setToken(token);
      api.getMe()
        .then((data) => setInspector(data.inspector))
        .catch(() => {
          setToken(null);
          setInspector(null);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (pin: string) => {
    const data = await api.login(pin);
    setToken(data.token);
    setInspector(data.inspector);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {}
    setToken(null);
    setInspector(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        inspector,
        isAuthenticated: !!inspector,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
